import * as React from "react";
import "./CustomToolbar.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

import { styled } from "@mui/material/styles";
import {
  Toolbar,
  ToolbarButton,
  ExportCsv,
  ExportPrint,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  QuickFilterTrigger,
} from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import { Box, Button, Badge } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsIcon from "@mui/icons-material/Settings";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import IconButton from "@mui/material/IconButton";
import { ListItemIcon, ListItemText } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CodeIcon from "@mui/icons-material/Code";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BlockIcon from "@mui/icons-material/Block";

const StyledQuickFilter = styled(QuickFilter)({
  display: "grid",
  alignItems: "center",
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
  gridArea: "1 / 1",
  width: "min-content",
  height: "min-content",
  zIndex: 1,
  opacity: ownerState.expanded ? 0 : 1,
  pointerEvents: ownerState.expanded ? "none" : "auto",
  transition: theme.transitions.create(["opacity"]),
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
  gridArea: "1 / 1",
  overflowX: "clip",
  width: ownerState.expanded ? 260 : "var(--trigger-width)",
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(["width", "opacity"]),
}));

// Formata a data/hora da última sincronização no formato "Hoje às HH:MM"
const formatSyncLabel = (date, t) => {
  if (!date) return "";

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) {
    return `${t("toolbar.today")} ${t("toolbar.at")} ${time}`;
  }

  return `${date.toLocaleDateString("pt-BR")} ${t("toolbar.at")} ${time}`;
};

const CustomToolbar = ({
  title,
  docType = "nfe",
  canSelect,
  canAssign,
  canEnterQty,
  selectedCount = 0,
  lastSync,
  onAssign,
  onEnterQty,
  onSimulate,
  onContinue,
  onOpenDoc,
  onUpload,
  onShowDanfe,
  onConfigureColumns,
  onExportExcel,
  canDownloadBatch,
  onDownloadDanfeBatch,
  onDownloadXmlBatch,
  canDelete,
  onDelete,
  onConsultarStatus,
  onRejeitar,
}) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useAuth();
  const canDeleteNfe = hasRole("SUPER") || hasRole("ADMIN");
  const isCte = docType === "cte";
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
  const exportMenuTriggerRef = React.useRef(null);

  const [anchorProc, setAnchorProc] = React.useState(null);
  const [anchorOpt, setAnchorOpt] = React.useState(null);
  const [anchorDanfe, setAnchorDanfe] = React.useState(null);
  const [anchorUpload, setAnchorUpload] = React.useState(null);

  const [anchorExportMenu, setAnchorExportMenu] = React.useState(null);
  const [anchorRejeitar, setAnchorRejeitar] = React.useState(null);
  const rejeitarItemRef = React.useRef(null);

  function handleOpenExportMenu(event) {
    setAnchorExportMenu(event.currentTarget);
  }

  function handleCloseExportMenu() {
    setAnchorExportMenu(null);
  }

  return (
    <Toolbar className="nfeToolbar">
      <Typography className="gridTitle" sx={{ flex: 1, mx: 0.5 }}>
        {title}
      </Typography>

      {lastSync && (
        <Box className="syncBadge">
          <AccessTimeIcon fontSize="small" />
          <Typography component="span">{formatSyncLabel(lastSync, t)}</Typography>
        </Box>
      )}

      <Box className="toolbarLeft">
        {!isCte && (
          <>
            <Badge
              badgeContent={selectedCount}
              color="primary"
              className="toolbarBadge"
            >
              <ToolbarButton
                className="sapLinkBtn"
                onClick={(e) => setAnchorProc(e.currentTarget)}
              >
                {t("buttons.processing")}
                <KeyboardArrowDownIcon fontSize="small" />
              </ToolbarButton>
            </Badge>

            <Menu
              anchorEl={anchorProc}
              open={Boolean(anchorProc)}
              onClose={() => setAnchorProc(null)}
            >
              <MenuItem
                className="sapLinkBtn"
                disabled={!canSelect}
                onClick={() => {
                  setAnchorProc(null);
                  onContinue?.();
                }}
              >
                {t("buttons.continue")}
              </MenuItem>
              <MenuItem
                className="sapLinkBtn"
                disabled={!canAssign}
                onClick={() => {
                  setAnchorProc(null);
                  onAssign?.();
                }}
              >
                {t("buttons.assign")}
              </MenuItem>
              <MenuItem
                className="sapLinkBtn"
                disabled={!canEnterQty}
                onClick={() => {
                  setAnchorProc(null);
                  onEnterQty?.();
                }}
              >
                {t("buttons.enterqty")}
              </MenuItem>
            </Menu>
          </>
        )}

        <Badge
          badgeContent={selectedCount}
          color="primary"
          className="toolbarBadge"
        >
          <ToolbarButton
            className="sapLinkBtn"
            onClick={(e) => setAnchorDanfe(e.currentTarget)}
          >
            {t(isCte ? "buttons.menuDacte" : "buttons.menuDanfe")}
            <KeyboardArrowDownIcon fontSize="small" />
          </ToolbarButton>
        </Badge>

        <Menu
          anchorEl={anchorDanfe}
          open={Boolean(anchorDanfe)}
          onClose={() => setAnchorDanfe(null)}
        >
          <MenuItem
            className="sapLinkBtn"
            onClick={() => {
              setAnchorUpload(null);
              onUpload?.();
            }}
          >
            {t("buttons.uploadXml")}
          </MenuItem>
          <MenuItem
            className="sapLinkBtn"
            disabled={!canSelect}
            onClick={() => {
              setAnchorDanfe(null);
              onShowDanfe?.();
            }}
          >
            {t(isCte ? "buttons.ShowDacte" : "buttons.ShowDanfe")}
          </MenuItem>
          {canDeleteNfe && (
            <MenuItem
              className="sapLinkBtn"
              disabled={!canDelete}
              onClick={() => {
                setAnchorDanfe(null);
                onDelete?.();
              }}
            >
              {t(isCte ? "buttons.deleteCte" : "buttons.deleteItem")}
            </MenuItem>
          )}

          <MenuItem
            className="sapLinkBtn"
            disabled={isCte || !canSelect}
            onClick={() => {
              setAnchorDanfe(null);
              onConsultarStatus?.();
            }}
          >
            {t("buttons.consultarStatus")}
          </MenuItem>

          {!isCte && (
            <MenuItem
              ref={rejeitarItemRef}
              className="sapLinkBtn"
              disabled={!canSelect}
              onClick={(e) => {
                setAnchorRejeitar(e.currentTarget);
              }}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>{t("buttons.rejeitar")}</span>
              <ChevronRightIcon fontSize="small" sx={{ ml: 1 }} />
            </MenuItem>
          )}

          {!isCte && (
            <MenuItem
              className="sapLinkBtn"
              disabled={!canSelect}
              onClick={() => {
                setAnchorDanfe(null);
                // onEnterDanfe?.();
              }}
            >
              {t("buttons.bEnablePhysicalReceipt")}
            </MenuItem>
          )}
        </Menu>

        <Menu
          anchorEl={anchorRejeitar}
          open={Boolean(anchorRejeitar)}
          onClose={() => setAnchorRejeitar(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem
            className="sapLinkBtn"
            onClick={() => {
              setAnchorRejeitar(null);
              setAnchorDanfe(null);
              onRejeitar?.("210220");
            }}
          >
            <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t("buttons.rejeitarDesconhecimento")}</ListItemText>
          </MenuItem>
          <MenuItem
            className="sapLinkBtn"
            onClick={() => {
              setAnchorRejeitar(null);
              setAnchorDanfe(null);
              onRejeitar?.("210240");
            }}
          >
            <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t("buttons.rejeitarNaoRealizada")}</ListItemText>
          </MenuItem>
        </Menu>

        {/* <Tooltip title={!canDelete ? "Selecione ao menos uma NF-e" : ""}>
          <span>
            <ToolbarButton
              className="sapLinkBtn"
              disabled={!canDelete}
              onClick={() => onDelete?.()}
              sx={{ color: canDelete ? "error.main" : undefined }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 0.5 }} />
              {t("buttons.deleteItem")}
            </ToolbarButton>
          </span>
        </Tooltip> */}

        {!isCte && (
          <ToolbarButton
            className="sapLinkBtn"
            // disabled={canOpenDocs === false}
            onClick={() => onOpenDoc?.()}
          >
            {t("buttons.documents")}
          </ToolbarButton>
        )}

        <ToolbarButton
          className="sapLinkBtn"
          onClick={(e) => setAnchorOpt(e.currentTarget)}
          sx={{ display: "none" }}
        >
          {t("buttons.customization")}
          <KeyboardArrowDownIcon fontSize="small" />
        </ToolbarButton>

        <Menu
          anchorEl={anchorOpt}
          open={Boolean(anchorOpt)}
          onClose={() => setAnchorOpt(null)}
        >
          <MenuItem className="sapLinkBtn">{t("buttons.material")}</MenuItem>
          <MenuItem className="sapLinkBtn">{t("buttons.unit")}</MenuItem>
          <MenuItem className="sapLinkBtn">{t("buttons.reference")}</MenuItem>
          <MenuItem className="sapLinkBtn">{t("buttons.redetermine")}</MenuItem>
        </Menu>
      </Box>

      <Tooltip title="Export">
        <ToolbarButton
          ref={exportMenuTriggerRef}
          id="export-menu-trigger"
          aria-controls="export-menu"
          aria-haspopup="true"
          aria-expanded={exportMenuOpen ? "true" : undefined}
          onClick={() => setExportMenuOpen(true)}
        ></ToolbarButton>
      </Tooltip>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{ mx: 0.5 }}
      />

      <Tooltip title={t("buttons.export")}>
        <IconButton onClick={handleOpenExportMenu}>
          <FileDownloadIcon fontSize="small" className="toolbarIconPrimary" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorExportMenu}
        open={Boolean(anchorExportMenu)}
        onClose={handleCloseExportMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleCloseExportMenu();
            onExportExcel?.();
          }}
        >
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontFamily: "'72', Arial, sans-serif", fontSize: 13 } }}>{t("buttons.exportExcel")}</ListItemText>
        </MenuItem>

        <MenuItem
          // disabled={!canDownloadBatch}
          onClick={() => {
            handleCloseExportMenu();
            onDownloadDanfeBatch?.();
          }}
        >
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontFamily: "'72', Arial, sans-serif", fontSize: 13 } }}>{t("buttons.downAllDanfe")}</ListItemText>
        </MenuItem>

        <MenuItem
          // disabled={!canDownloadBatch}
          onClick={() => {
            handleCloseExportMenu();
            onDownloadXmlBatch?.();
          }}
        >
          <ListItemIcon>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { fontFamily: "'72', Arial, sans-serif", fontSize: 13 } }}>{t("buttons.downAllXml")}</ListItemText>
        </MenuItem>
      </Menu>

      <Tooltip title={t("buttons.settings")}>
        <IconButton size="small" onClick={onConfigureColumns}>
          <SettingsIcon fontSize="small" className="toolbarIconNeutral" />
        </IconButton>
      </Tooltip>

      <Menu
        id="export-menu"
        anchorEl={exportMenuTriggerRef.current}
        open={exportMenuOpen}
        onClose={() => setExportMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          list: {
            "aria-labelledby": "export-menu-trigger",
          },
        }}
      >
        <ExportPrint
          render={<MenuItem />}
          onClick={() => setExportMenuOpen(false)}
        >
          {t("buttons.print")}
        </ExportPrint>
        <ExportCsv render={<MenuItem />} onClick={() => onExportExcel(false)}>
          {t("buttons.download")}
        </ExportCsv>
      </Menu>

      <StyledQuickFilter>
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title="Search" enterDelay={0}>
              <StyledToolbarButton
                {...triggerProps}
                ownerState={{ expanded: state.expanded }}
                color="default"
                aria-disabled={state.expanded}
              >
                <SearchIcon fontSize="small" className="toolbarIconNeutral" />
              </StyledToolbarButton>
            </Tooltip>
          )}
        />
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              ownerState={{ expanded: state.expanded }}
              inputRef={ref}
              aria-label="Search"
              placeholder="Search..."
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label="Clear search"
                        material={{ sx: { marginRight: -0.75 } }}
                      >
                        <CancelIcon fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...controlProps.slotProps?.input,
                },
                ...controlProps.slotProps,
              }}
            />
          )}
        />
      </StyledQuickFilter>
    </Toolbar>
  );
};

export default CustomToolbar;